import React, { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { ArrowLeft, Search, Loader2 } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { useSettings, resolveGymTimezone, formatInGymTimezone, fmtDate } from "@/lib/utils-app";
import {
  playAttendanceAlertSound,
  playAttendanceSuccessSound,
  unlockAttendanceAudio,
} from "@/lib/attendanceSound";
import { notifyAttendanceUpdated } from "@/lib/attendanceSync";
import AttendanceSuccessOverlay from "@/components/AttendanceSuccessOverlay";
import {
  loadFaceModels,
  descriptorFromVideo,
  evaluateMatch,
  captureFrameDataUrl,
} from "@/lib/faceRecognition";

const SUCCESS_DISPLAY_MS = 12000;
const SCAN_DEBOUNCE_MS = 4000;
// Frames consecutivos que deben coincidir en la misma persona antes de registrar.
const CONSENSUS_FRAMES = 3;
// Espera minima entre verificaciones con IA para el mismo par de candidatos.
const AI_VERIFY_COOLDOWN_MS = 6000;
const KIOSK_HINT = "Mira a la cámara para registrar tu asistencia del día";

function buildDisplayFromResponse(data, catalogEntry) {
  if (data?.display?.full_name) return data.display;
  const nowIso = data?.check_in_at || new Date().toISOString();
  return {
    action: data?.action || "check_in",
    full_name: data?.subscriber_name || catalogEntry?.full_name || "Atleta",
    profile_photo: catalogEntry?.profile_photo || null,
    package_name: null,
    enrolled_at: catalogEntry?.enrolled_at || null,
    enrolled_date_label: catalogEntry?.enrolled_at ? fmtDate(catalogEntry.enrolled_at) : null,
    days_since_enrollment: null,
    next_due_date: null,
    days_until_payment: null,
    shift_name: data?.shift_name || null,
    check_in_at: nowIso,
    check_out_at: data?.check_out_at || null,
    training_hours: data?.training_hours ?? null,
    billing_comment: data?.billing_comment || null,
  };
}

export default function AsistenciaCheckIn() {
  const { user } = useAuth();
  const settings = useSettings();
  const gymTz = resolveGymTimezone(settings);

  const [clockNow, setClockNow] = useState(() => new Date());
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const scanRef = useRef(null);
  /** Solo se actualiza tras un check-in exitoso o rechazado (409). */
  const lastCheckInRef = useRef({ id: null, at: 0 });
  const successTimerRef = useRef(null);
  /** Consenso multi-frame: identidad candidata y cuantos frames seguidos coincide. */
  const consensusRef = useRef({ id: null, count: 0 });
  /** Control de verificacion con IA en curso y enfriamiento por par de candidatos. */
  const aiVerifyRef = useRef({ inFlight: false, key: null, at: 0 });

  const [catalog, setCatalog] = useState([]);
  const [status, setStatus] = useState("init");
  const [message, setMessage] = useState("Iniciando cámara…");
  const [manualOpen, setManualOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [busy, setBusy] = useState(false);
  const [successDisplay, setSuccessDisplay] = useState(null);

  useEffect(() => {
    const unlock = () => unlockAttendanceAudio();
    document.addEventListener("click", unlock, { once: true, capture: true });
    document.addEventListener("touchstart", unlock, { once: true, capture: true });
    return () => {
      document.removeEventListener("click", unlock, { capture: true });
      document.removeEventListener("touchstart", unlock, { capture: true });
    };
  }, []);

  const resumeCamera = useCallback(async () => {
    const video = videoRef.current;
    let stream = streamRef.current;
    if (!stream || stream.getVideoTracks().every((t) => t.readyState === "ended")) {
      try {
        stream = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: "user", width: { ideal: 640 }, height: { ideal: 480 } },
          audio: false,
        });
        streamRef.current = stream;
      } catch {
        setStatus("error");
        setMessage("No se pudo reactivar la cámara. Usa búsqueda manual.");
        return;
      }
    }
    if (video && stream) {
      video.srcObject = stream;
      try {
        await video.play();
      } catch {
        /* reintento en el siguiente frame */
        requestAnimationFrame(() => video.play().catch(() => {}));
      }
    }
  }, []);

  const clearSuccess = useCallback(() => {
    if (successTimerRef.current) {
      clearTimeout(successTimerRef.current);
      successTimerRef.current = null;
    }
    setSuccessDisplay(null);
    setStatus("scanning");
    setMessage(KIOSK_HINT);
    resumeCamera();
  }, [resumeCamera]);

  const showSuccess = useCallback((display) => {
    setSuccessDisplay(display);
    setStatus("success");
    setMessage("");
    const isEarlyRejected = display.action === "early_rejected";
    const isAlreadyToday = display.action === "already_today";
    const isUnpaidEntry = display.payment_alert && display.action === "check_in";
    if (isAlreadyToday) {
      playAttendanceAlertSound();
      toast.info(
        `${display.full_name}: ${display.message || "Ya registró asistencia hoy."}`,
        { duration: 10000 },
      );
    } else if (isEarlyRejected) {
      playAttendanceAlertSound();
      toast.warning(
        `${display.full_name}: ${display.billing_comment || "Llegada anticipada — no cuenta como entrada"}`,
        { duration: 10000 },
      );
    } else if (isUnpaidEntry) {
      playAttendanceAlertSound();
      toast.warning(
        `${display.full_name}: ${display.payment_alert_message || "Sin inscripción ni mensualidad"}`,
        { duration: 10000 },
      );
    } else {
      playAttendanceSuccessSound();
    }
    const displayMs = isUnpaidEntry || isEarlyRejected || isAlreadyToday ? 18000 : SUCCESS_DISPLAY_MS;
    if (successTimerRef.current) clearTimeout(successTimerRef.current);
    successTimerRef.current = setTimeout(clearSuccess, displayMs);
  }, [clearSuccess]);

  useEffect(() => {
    const tick = setInterval(() => setClockNow(new Date()), 1000);
    return () => clearInterval(tick);
  }, []);

  useEffect(() => () => {
    if (successTimerRef.current) clearTimeout(successTimerRef.current);
  }, []);

  const clockTime = formatInGymTimezone(clockNow, gymTz, {
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: false,
  });
  const clockDate = formatInGymTimezone(clockNow, gymTz, {
    weekday: "long",
    day: "numeric",
    month: "long",
    year: "numeric",
  });

  useEffect(() => {
    api
      .get("/attendance/catalog")
      .then((r) => setCatalog(r.data || []))
      .catch(() => toast.error("No se pudo cargar catálogo"));
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        await loadFaceModels();
        const stream = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: "user", width: { ideal: 640 }, height: { ideal: 480 } },
          audio: false,
        });
        if (cancelled) {
          stream.getTracks().forEach((t) => t.stop());
          return;
        }
        streamRef.current = stream;
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
          await videoRef.current.play();
        }
        setStatus("scanning");
        setMessage(KIOSK_HINT);
      } catch {
        setStatus("error");
        setMessage("No se pudo acceder a la cámara. Usa búsqueda manual.");
      }
    })();
    return () => {
      cancelled = true;
      if (scanRef.current) clearInterval(scanRef.current);
      streamRef.current?.getTracks().forEach((t) => t.stop());
    };
  }, []);

  const doCheckIn = useCallback(async (subscriberId, method, confidence = null) => {
    if (busy || successDisplay) return;

    const catalogEntry = catalog.find((c) => c.id === subscriberId);
    setBusy(true);
    setMessage("Registrando…");

    try {
      const { data } = await api.post("/attendance/check-in", {
        subscriber_id: subscriberId,
        method,
        confidence,
      });
      lastCheckInRef.current = { id: subscriberId, at: Date.now() };
      const display = {
        ...buildDisplayFromResponse(data, catalogEntry),
        ...(data?.message ? { message: data.message } : {}),
      };
      if (display.action !== "already_today") {
        notifyAttendanceUpdated();
      }
      showSuccess(display);
      if (display.action === "early_rejected" || display.action === "already_today") {
        /* toast ya mostrado en showSuccess */
      } else {
        toast.success(`Asistencia: ${display.full_name}`);
      }
    } catch (e) {
      const detail = e?.response?.data?.detail;
      const msg = typeof detail === "string" ? detail : "No se pudo registrar";
      setStatus("error");
      setMessage(msg);
      toast.error(msg);
      if (e?.response?.status === 409) {
        lastCheckInRef.current = { id: subscriberId, at: Date.now() };
      }
      setTimeout(() => {
        setStatus("scanning");
        setMessage(KIOSK_HINT);
      }, 5000);
    } finally {
      setBusy(false);
    }
  }, [busy, successDisplay, catalog, gymTz, showSuccess]);

  /** Verificacion de respaldo con IA cuando face-api duda entre 2 candidatos. */
  const verifyAmbiguousWithAI = useCallback(async (result) => {
    const candidateIds = [result.subscriber?.id, result.runnerUp?.id].filter(Boolean);
    if (!candidateIds.length) return;

    const key = candidateIds.slice().sort().join("|");
    const now = Date.now();
    if (
      aiVerifyRef.current.inFlight
      || (aiVerifyRef.current.key === key && now - aiVerifyRef.current.at < AI_VERIFY_COOLDOWN_MS)
    ) {
      return;
    }

    const image = captureFrameDataUrl(videoRef.current);
    if (!image) return;

    aiVerifyRef.current = { inFlight: true, key, at: now };
    setStatus("verifying");
    setMessage("Verificando identidad…");
    try {
      const { data } = await api.post("/attendance/verify-face", {
        image,
        candidate_ids: candidateIds,
      });
      const confirmedId = data?.subscriber_id || null;
      if (confirmedId) {
        consensusRef.current = { id: null, count: 0 };
        const entry = catalog.find((c) => c.id === confirmedId);
        setStatus("matched");
        setMessage(`Identificado: ${entry?.full_name || ""}`);
        await doCheckIn(confirmedId, "face_ai", data?.confidence ?? null);
      } else {
        setStatus("scanning");
        setMessage("Acércate y mira de frente a la cámara");
      }
    } catch {
      setStatus("scanning");
      setMessage(KIOSK_HINT);
    } finally {
      aiVerifyRef.current.inFlight = false;
    }
  }, [catalog, doCheckIn]);

  useEffect(() => {
    if (!catalog.length || successDisplay) return;

    scanRef.current = setInterval(async () => {
      if (
        busy
        || successDisplay
        || aiVerifyRef.current.inFlight
        || !videoRef.current
        || videoRef.current.readyState < 2
      ) {
        return;
      }
      try {
        const desc = await descriptorFromVideo(videoRef.current);
        if (!desc) {
          consensusRef.current = { id: null, count: 0 };
          return;
        }
        const result = evaluateMatch(desc, catalog);

        if (result.status === "none") {
          consensusRef.current = { id: null, count: 0 };
          return;
        }

        if (result.status === "ambiguous") {
          consensusRef.current = { id: null, count: 0 };
          await verifyAmbiguousWithAI(result);
          return;
        }

        // status === "confident": exigir varios frames seguidos con la misma persona.
        const subscriberId = result.subscriber.id;
        const now = Date.now();
        if (
          lastCheckInRef.current.id === subscriberId
          && now - lastCheckInRef.current.at < SCAN_DEBOUNCE_MS
        ) {
          return;
        }

        if (consensusRef.current.id === subscriberId) {
          consensusRef.current.count += 1;
        } else {
          consensusRef.current = { id: subscriberId, count: 1 };
        }

        if (consensusRef.current.count < CONSENSUS_FRAMES) {
          setStatus("matched");
          setMessage(`Identificando: ${result.subscriber.full_name}…`);
          return;
        }

        consensusRef.current = { id: null, count: 0 };
        setStatus("matched");
        setMessage(`Identificado: ${result.subscriber.full_name}`);
        await doCheckIn(subscriberId, "face", result.distance);
      } catch {
        /* ignore frame errors */
      }
    }, 1200);

    return () => clearInterval(scanRef.current);
  }, [catalog, busy, successDisplay, doCheckIn, verifyAmbiguousWithAI]);

  const manualList = catalog.filter((s) =>
    s.full_name.toLowerCase().includes(search.toLowerCase()),
  );

  return (
    <div className="h-[100dvh] max-h-[100dvh] overflow-hidden bg-[#0A0A0A] text-white flex flex-col">
      <header className="flex items-center justify-between px-4 py-3 border-b border-white/10">
        <div className="font-display text-xl text-green-400">Asistencia</div>
        <div className="text-xs text-white/60">{user?.email}</div>
        <button
          type="button"
          onClick={() => {
            if (window.opener) {
              window.close();
            } else {
              window.location.href = "/admin/asistencia";
            }
          }}
          className="btn-outline text-xs py-1.5"
        >
          <ArrowLeft size={14} /> Volver al menú
        </button>
      </header>

      <div className="shrink-0 text-center py-2 sm:py-3 px-4 border-b border-white/10 bg-gradient-to-b from-green-950/40 to-transparent">
        <div
          className="font-display text-5xl sm:text-6xl md:text-7xl text-[#FFD700] tracking-tight tabular-nums leading-none"
          aria-live="polite"
        >
          {clockTime}
        </div>
        <div className="text-sm sm:text-base text-white/60 mt-2 capitalize">{clockDate}</div>
        <div className="text-xs text-white/40 mt-1 uppercase tracking-widest">
          Hora del gimnasio · {gymTz.replace("America/", "").replace(/_/g, " ")}
        </div>
      </div>

      <div className="flex-1 min-h-0 flex flex-col items-center justify-center p-3 sm:p-4">
        <div className="relative flex-1 min-h-0 w-full max-w-3xl bg-black rounded-sm overflow-hidden border-2 border-green-600/40">
          <video
            ref={videoRef}
            className={`absolute inset-0 w-full h-full object-cover mirror ${
              successDisplay ? "invisible" : ""
            }`}
            playsInline
            muted
          />
          {status === "scanning" && !successDisplay && (
            <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
              <div className="w-48 h-56 border-2 border-green-400/60 rounded-lg" />
            </div>
          )}
          {busy && !successDisplay && (
            <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
              <Loader2 className="animate-spin text-green-400" size={40} />
            </div>
          )}
        </div>
        {!successDisplay && message && (
          <p className={`shrink-0 mt-2 text-center text-base sm:text-lg font-medium max-w-xl ${
            status === "error" ? "text-red-400" : "text-white/60"
          }`}>
            {message}
          </p>
        )}
        {!successDisplay && (
          <button
            type="button"
            onClick={() => {
              unlockAttendanceAudio();
              setManualOpen(true);
            }}
            disabled={busy}
            className="shrink-0 mt-2 btn-outline text-sm flex items-center gap-2 disabled:opacity-40"
          >
            <Search size={16} /> Búsqueda manual
          </button>
        )}
      </div>

      {manualOpen && (
        <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4" onClick={() => setManualOpen(false)}>
          <div className="glass-strong w-full max-w-md rounded-sm p-5" onClick={(e) => e.stopPropagation()}>
            <h3 className="font-display text-xl text-white mb-3">Buscar atleta</h3>
            <input
              className="input-dark w-full"
              placeholder="Nombre…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              autoFocus
            />
            <ul className="mt-3 max-h-64 overflow-y-auto space-y-1">
              {manualList.slice(0, 20).map((s) => (
                <li key={s.id}>
                  <button
                    type="button"
                    className="w-full text-left px-3 py-2 rounded-sm hover:bg-white/10 text-white"
                    onClick={() => {
                      setManualOpen(false);
                      unlockAttendanceAudio();
                      doCheckIn(s.id, "manual");
                    }}
                  >
                    {s.full_name}
                  </button>
                </li>
              ))}
            </ul>
          </div>
        </div>
      )}

      {successDisplay && (
        <AttendanceSuccessOverlay
          display={successDisplay}
          gymTz={gymTz}
          onDismiss={() => {
            unlockAttendanceAudio();
            clearSuccess();
          }}
        />
      )}

      <style>{`.mirror { transform: scaleX(-1); }`}</style>
    </div>
  );
}
